import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { fitArtwork, visibleBounds } from './evermarkd-artwork-geometry.js';

const ns = 'http://www.w3.org/2000/svg';
const element = (tag, attributes) => {
  const node = document.createElementNS(ns, tag);
  for (const [key, value] of Object.entries(attributes)) node.setAttribute(key, String(value));
  return node;
};
let instance = 0;

export function layoutArea(handle, patchShape = 'Rectangle', config) {
  if (handle === 'personalized-custom-leatherette-patch-hat') {
    const keys = { Rectangle: 'rectangle', 'Rounded Square': 'rounded-square', Circle: 'circle', Oval: 'oval', Hexagon: 'hexagon' };
    const patch = config?.artwork?.[`personalized-patch-${keys[patchShape]}`];
    if (!patch?.patchSizeInches) return null;
    const width = patch.patchSizeInches.width * 25.4;
    const height = patch.patchSizeInches.height * 25.4;
    return { x: 0, y: 0, width, height, shape: ({ Circle: 'circle', Oval: 'ellipse', Hexagon: 'hexagon' })[patchShape] || 'roundedrect', radius: Math.min(width, height) * .08, inset: Math.min(width, height) * .15, units: 'mm', id: `patch-${keys[patchShape]}-v1`, patch: true };
  }
  if (handle === 'personalized-wooden-nfc-business-cards-10-pack') return { x: 0, y: 0, width: 85, height: 54, shape: 'roundedrect', radius: 3, inset: 3, units: 'mm', id: 'wood-nfc-85x54-v1' };
  if (handle.includes('pens-10-pack')) return { x: 0, y: 0, width: 100, height: 12, shape: 'roundedrect', radius: 3, inset: 2, units: 'relative', id: 'pen-suggested-panel-v1' };
  if (handle === 'custom-engraved-aluminum-business-card-50-count') return { x: 0, y: 0, width: 100, height: 64, shape: 'roundedrect', radius: 4, inset: 5, units: 'relative', id: 'metal-card-suggested-panel-v1' };
  if (handle === 'bulk-aluminum-pet-tags' || handle.includes('dog-tag')) return { x: 0, y: 0, width: 100, height: 60, shape: 'ellipse', inset: 12, units: 'relative', id: 'tag-center-panel-v1' };
  if (handle === 'custom-engraved-bar-blade') return { x: 0, y: 0, width: 100, height: 30, shape: 'roundedrect', radius: 4, inset: 5, units: 'relative', id: 'bar-blade-center-panel-v1' };
  if (handle === 'custom-engraved-coaster') return { x: 0, y: 0, width: 100, height: 100, shape: 'circle', inset: 10, units: 'relative', id: 'coaster-suggested-panel-v1' };
  return { x: 0, y: 0, width: 70, height: 100, shape: 'rect', inset: 8, units: 'relative', id: 'drinkware-suggested-panel-v1' };
}

class EvermarkdArtworkLayout extends Component {
  #controller;
  #scope;
  #form;
  #labels;
  #config;
  #product;
  #variant;
  #generation = 0;
  #sides = new Map();
  #id = ++instance;

  connectedCallback() { super.connectedCallback(); this.#bind(); }
  updatedCallback() { super.updatedCallback(); this.#bind(); }
  disconnectedCallback() {
    this.#controller?.abort();
    this.#generation++;
    for (const state of this.#sides.values()) { state.generation++; if (state.url) URL.revokeObjectURL(state.url); }
    this.#sides.clear();
    super.disconnectedCallback();
  }

  async #bind() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.#form = this.closest('form');
    this.#scope = this.closest('.shopify-section, dialog') || this.#form;
    if (!this.#form || !this.#scope) return;
    this.#labels = JSON.parse(this.querySelector('[data-layout-labels]').textContent);
    this.#product = JSON.parse(this.querySelector('[data-layout-product]').textContent);
    this.#variant = this.#product.variants.find(v => String(v.id) === String(this.#form.querySelector('[name="id"]')?.value || this.#product.initial));
    for (const side of this.querySelectorAll('[data-layout-side]')) {
      if (!this.#sides.has(side.dataset.layoutSide)) this.#sides.set(side.dataset.layoutSide, { side, percent: 80, generation: 0 });
      else this.#sides.get(side.dataset.layoutSide).side = side;
    }
    this.#form.addEventListener('change', this.#change, options);
    this.addEventListener('input', this.#input, options);
    this.addEventListener('click', this.#click, options);
    this.#form.addEventListener('submit', this.#submit, { ...options, capture: true });
    this.#scope.addEventListener(StandardEvents.productSelect, this.#selection, options);
    this.#scope.addEventListener('change', this.#shapeChange, options);
    this.#syncSides();
    const generation = ++this.#generation;
    if (this.dataset.handle === 'personalized-custom-leatherette-patch-hat') {
      try {
        const response = await fetch(this.dataset.geometry, { signal: this.#controller.signal });
        if (!response.ok) throw new Error('Geometry unavailable');
        const config = await response.json();
        if (!this.isConnected || this.#controller.signal.aborted) return;
        this.#config = config;
      } catch { if (generation !== this.#generation) return; }
    }
    for (const state of this.#sides.values()) {
      const file = this.#fileInput(state)?.files?.[0];
      if (file && file !== state.file) this.#load(state);
      else this.#render(state);
    }
  }

  #fileInput(state) { return state.side.dataset.layoutSide === 'front' ? this.#form.querySelector('input[type="file"][name="properties[Artwork or photo]"]') : state.side.querySelector('[data-back-file]'); }
  #area() { return layoutArea(this.dataset.handle, this.#form.querySelector('[data-personalized-patch-shape]:checked')?.value, this.#config); }
  #double() { return this.dataset.handle === 'custom-engraved-aluminum-business-card-50-count' || this.#variant?.options?.some(value => /^double-sided$/i.test(value)) || false; }

  #syncSides() {
    const back = this.#sides.get('back');
    if (back) { back.side.hidden = !this.#double(); back.side.disabled = !this.#double(); }
    for (const state of this.#sides.values()) this.#render(state);
  }

  #selection = async event => {
    if (event.target instanceof Element && event.target.closest('product-card')) return;
    const generation = ++this.#generation;
    try {
      const { detail } = await event.promise;
      if (!this.isConnected || generation !== this.#generation) return;
      const variant = this.#product.variants.find(v => String(v.id) === String(detail?.resource?.id));
      if (!variant) return;
      this.#variant = variant;
      this.#syncSides();
      this.dispatchEvent(new Event('input', { bubbles: true }));
    } catch { /* Keep the current layout while the native variant picker handles errors. */ }
  };

  #shapeChange = event => {
    if (event.target.matches?.('[data-personalized-patch-shape]')) for (const state of this.#sides.values()) this.#render(state);
  };
  #change = event => {
    for (const state of this.#sides.values()) {
      if (event.target === this.#fileInput(state)) { this.#load(state); break; }
      if (event.target === state.side.querySelector('[data-layout-trim]')) { this.#prepare(state); break; }
    }
  };
  #input = event => {
    if (!event.target.matches?.('[data-layout-scale]')) return;
    const state = this.#sides.get(event.target.closest('[data-layout-side]').dataset.layoutSide);
    state.percent = Math.min(100, Math.max(10, Number(event.target.value) || 80));
    this.#render(state);
  };
  #click = event => {
    const button = event.target.closest?.('[data-layout-step], [data-layout-reset]');
    if (!button) return;
    const state = this.#sides.get(button.closest('[data-layout-side]').dataset.layoutSide);
    if (!state.image || state.pending || !this.#area()) return;
    state.percent = button.hasAttribute('data-layout-reset') ? 80 : Math.min(100, Math.max(10, state.percent + Number(button.dataset.layoutStep)));
    this.#render(state);
    this.dispatchEvent(new Event('input', { bubbles: true }));
  };
  #submit = event => {
    const state = [...this.#sides.values()].find(s => !s.side.disabled && s.pending);
    if (!state) return;
    event.preventDefault(); event.stopImmediatePropagation();
    const input = this.#fileInput(state);
    input?.setCustomValidity(this.#labels.loading); input?.reportValidity();
  };

  async #load(state) {
    const generation = ++state.generation;
    const input = this.#fileInput(state);
    const file = input?.files?.[0];
    input?.setCustomValidity('');
    state.file = file; state.image = null; state.bounds = null; state.pending = false; state.percent = 80;
    state.side.querySelector('[data-layout-trim]').checked = false;
    if (state.url) URL.revokeObjectURL(state.url);
    state.url = null;
    if (!file) { state.message = 'empty'; this.#render(state); return; }
    if (file.size > 10 * 1024 * 1024 || !/\.(png|jpe?g|pdf)$/i.test(file.name)) {
      input.setCustomValidity(this.#labels.file_error);
      state.message = 'failed'; this.#render(state); return;
    }
    if (/\.pdf$/i.test(file.name)) { state.message = 'pdf'; this.#render(state); return; }
    state.pending = true; state.message = 'loading'; this.#render(state);
    queueMicrotask(() => { if (state.pending && generation === state.generation) input.setCustomValidity(this.#labels.loading); });
    const source = URL.createObjectURL(file);
    try {
      const image = new Image();
      image.src = source;
      await image.decode();
      if (!this.isConnected || generation !== state.generation) return;
      if (!image.naturalWidth || !image.naturalHeight || image.naturalWidth * image.naturalHeight > 100000000) throw new Error('Image dimensions');
      const ratio = Math.min(1, 2200 / Math.max(image.naturalWidth, image.naturalHeight));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(image.naturalWidth * ratio)); canvas.height = Math.max(1, Math.round(image.naturalHeight * ratio));
      const context = canvas.getContext('2d', { willReadFrequently: true });
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      state.image = canvas; state.originalSize = [image.naturalWidth, image.naturalHeight];
      state.pixels = context.getImageData(0, 0, canvas.width, canvas.height);
      state.message = 'ready';
      await this.#prepare(state, generation);
    } catch {
      if (generation === state.generation) { state.image = null; state.message = 'failed'; }
    } finally {
      URL.revokeObjectURL(source);
      if (generation === state.generation) { state.pending = false; input.setCustomValidity(''); this.#render(state); }
    }
  }

  async #prepare(state, generation = ++state.generation) {
    if (!state.image || !state.pixels) return;
    state.pending = true;
    try {
      const trim = state.side.querySelector('[data-layout-trim]').checked;
      const bounds = visibleBounds(state.pixels, { trimSolid: trim });
      state.bounds = bounds;
      if (!bounds) { state.message = 'transparent'; return; }
      const canvas = document.createElement('canvas'); canvas.width = bounds.width; canvas.height = bounds.height;
      canvas.getContext('2d').drawImage(state.image, bounds.x, bounds.y, bounds.width, bounds.height, 0, 0, bounds.width, bounds.height);
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      if (!this.isConnected || generation !== state.generation) return;
      if (!blob) throw new Error('Preview export unavailable');
      if (state.url) URL.revokeObjectURL(state.url);
      state.url = URL.createObjectURL(blob);
    } catch {
      if (generation === state.generation) {
        if (state.url) URL.revokeObjectURL(state.url);
        state.url = null; state.bounds = null; state.message = 'failed';
      }
    } finally {
      if (generation === state.generation) {
        state.pending = false;
        this.#fileInput(state)?.setCustomValidity('');
        this.#render(state);
        this.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  }

  #render(state) {
    const area = this.#area();
    const side = state.side;
    const ready = Boolean(area && state.url && state.bounds && !state.pending);
    const active = !side.disabled;
    const tools = side.querySelector('[data-layout-tools]');
    if (tools) tools.hidden = !ready;
    const range = side.querySelector('[data-layout-scale]'); range.value = state.percent; range.disabled = !ready;
    for (const button of side.querySelectorAll('button')) button.disabled = !ready;
    side.querySelector('[data-layout-percent]').value = `${state.percent}%`;
    side.querySelector('[data-layout-trim]').disabled = !state.image || state.pending;
    side.querySelector('[data-layout-area]').textContent = this.#labels[area?.patch ? 'patch_area' : 'suggested'];
    const message = !area ? 'contour' : state.pending ? 'loading' : state.message || 'empty';
    const quality = ready && Math.min(state.bounds.width, state.bounds.height) < 250;
    side.querySelector('[data-layout-status]').textContent = this.#labels[message] + (quality ? ` ${this.#labels.quality}` : '');
    const summary = side.querySelector('[data-layout-summary]'); const metadata = side.querySelector('[data-layout-metadata]');
    summary.disabled = metadata.disabled = !ready || !active;
    summary.value = ''; metadata.value = '';
    const margin = area ? Math.max(area.width, area.height) * .08 : 8;
    const viewBox = area ? `${-margin} ${-margin} ${area.width + 2 * margin} ${area.height + 2 * margin}` : '-8 -8 116 116';
    const svg = element('svg', { viewBox, role: 'img', 'aria-label': `${this.#labels[side.dataset.layoutSide]}: ${this.#labels.suggested}` });
    const panel = element('g', {});
    if (area) {
      const { width: w, height: h, shape } = area;
      const colors = { Black: '#303030', 'Dark Blue': '#243751', Blue: '#356ca8', 'Light Blue': '#8cbed1', 'Dark Green': '#315447', 'Light Green': '#93b886', Purple: '#755087', 'Light Purple': '#b4a0c4', Orange: '#de8b4b', Peach: '#e8b09a', Red: '#b84d4b', Yellow: '#e0be53', Maple: '#dfc399', Cherry: '#c58a60', 'Black Walnut': '#765747', Natural: '#dfc399', 'Golden Brown': '#b7854d', 'Medium Brown': '#946744', 'Dark Brown': '#634739', 'Red Brown': '#9b5944' };
      const fill = colors[this.#variant?.options?.[0]] || '#d8d3ca';
      const attrs = { fill, stroke: '#65513b', 'stroke-width': Math.min(w, h) * .008 };
      if (shape === 'ellipse' || shape === 'circle') panel.append(element('ellipse', { cx: w / 2, cy: h / 2, rx: w / 2, ry: h / 2, ...attrs }));
      else if (shape === 'hexagon') panel.append(element('polygon', { points: `${w / 4},0 ${w * .75},0 ${w},${h / 2} ${w * .75},${h} ${w / 4},${h} 0,${h / 2}`, ...attrs }));
      else panel.append(element('rect', { width: w, height: h, rx: area.radius || 0, ...attrs }));
      if (ready) {
        const fit = fitArtwork(state.bounds.width / state.bounds.height, area, state.percent);
        if (fit) {
          panel.append(element('image', { href: state.url, ...fit, preserveAspectRatio: 'xMidYMid meet' }));
          const trim = side.querySelector('[data-layout-trim]').checked;
          const dimensions = area.units === 'mm' ? `${fit.width.toFixed(1)} × ${fit.height.toFixed(1)} mm; ` : '';
          summary.value = `${dimensions}${state.percent}%; ${this.#labels.centered}; ${this.#labels[trim ? 'trimmed' : 'original']}`;
          metadata.value = JSON.stringify({ version: 1, template: area.id, units: area.units, suggested: true, original: state.originalSize, bounds: { x: state.bounds.x / state.image.width, y: state.bounds.y / state.image.height, width: state.bounds.width / state.image.width, height: state.bounds.height / state.image.height }, percent: state.percent, area, fit, trimWhite: trim, variant: String(this.#variant?.id || '') });
          if (side.dataset.layoutSide === 'front' && active) this.#scope.dispatchEvent(new CustomEvent('evermarkd:artwork-layout', { detail: { url: state.url, normalized: { x: fit.x / w, y: fit.y / h, width: fit.width / w, height: fit.height / h } } }));
        }
      } else if (side.dataset.layoutSide === 'front' && active) this.#scope.dispatchEvent(new CustomEvent('evermarkd:artwork-layout', { detail: null }));
      svg.append(panel);
    }
    side.querySelector('[data-layout-canvas]').replaceChildren(svg);
    summary.dispatchEvent(new Event('input', { bubbles: true }));
  }
}

if (!customElements.get('evermarkd-artwork-layout')) customElements.define('evermarkd-artwork-layout', EvermarkdArtworkLayout);
